import React, { useEffect, useState } from 'react';
import { AppFeatures, canIUse } from '../../helpers/PermissionUtility';
import { DndProvider } from 'react-dnd';
import { FormTag } from '../tags-types';
import { Grid } from 'semantic-ui-react';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Provider, ReactReduxContext, connect } from 'react-redux';
import { saveFormTagChanges, createAndAddTag, createTag, getAllNestedTags } from '../actions';
import { useAppSelector } from '../../hooks/ReduxHooks';
import FormTags from './FormTags';
import GfModal from '../../gfcomponents/GfModal/GfModal';
import TagList from './TagList';
import Loader from '../../components/loader';

export type TagsModalProps = {
  formId: string;
  formName: string;
  onClose: (formTags?: FormTag[]) => void;
  saveFormTagChanges?: (formId: string, onClose: () => void) => void;
  createAndAddTag?: (id: string) => void;
  createTag?: (id: string) => void;
  getAllNestedTags?: () => void;
};

const mapDispatchToProps = {
  saveFormTagChanges,
  createAndAddTag,
  createTag,
  getAllNestedTags
};

function ConnectedTagsModal(props: TagsModalProps) {
  const [search, setSearch] = useState('');
  const saving = useAppSelector<boolean>((state) => !!state.tags?.saving);
  const savingTag = useAppSelector<boolean>((state) => !!state.tags?.savingTag);
  const canAddTags = canIUse(AppFeatures.TagAdd);
  const canCreateTags = canIUse(AppFeatures.TagCreate);

  useEffect(() => {
    // refresh the tags list every time the modal is opened
    props.getAllNestedTags();
  }, []);

  function searchChanged(e: React.ChangeEvent<HTMLInputElement>) {
    setSearch(e.target.value);
  }

  function searchKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && canCreateTags) {
      createTag();
    }
  }

  function createTag() {
    if (!!search?.trim() && canCreateTags) {
      if (canAddTags) {
        props.createAndAddTag(search);
      } else {
        props.createTag(search);
      }
    }

    setSearch('');
  }

  function handleSaveChangesClick() {
    props.saveFormTagChanges(props.formId, props.onClose);
  }

  function closeModal() {
    props.onClose(null);
  }

  return (
    <ReactReduxContext.Consumer>
      {(ctx) => (
        <DndProvider backend={HTML5Backend}>
          <Provider store={ctx.store}>
            <GfModal
              title={`Tags - ${props.formName}`}
              onClose={() => closeModal()}
              className="tags-modal"
              primaryText="Save Changes"
              onPrimaryClick={handleSaveChangesClick}
              tertiaryText="Cancel"
              onTertiaryClick={() => closeModal()}
              loading={saving}
              semanticUiModalProps={{ size: 'small' }}
            >
              <Grid divided="vertically">
                <Grid.Column width={8}>
                  <div className="tag-column">
                    <h3>Tags</h3>
                    <div className="select-add-label">{canCreateTags ? 'Select a tag or create a new tag' : 'Select a tag'}</div>
                    <div className="add-search">
                      <input
                        className="add-search-input"
                        value={search}
                        onChange={searchChanged}
                        onKeyDown={searchKeyDown}
                        style={{ paddingRight: canCreateTags ? '32px' : '8px' }}
                        maxLength={50}
                      />
                      {canCreateTags ? (
                        <button className="plain-button add-tag-button" onClick={createTag}>
                          <i className="icon plus circle" />
                        </button>
                      ) : null}
                    </div>
                    <TagList search={search} />
                    <Loader isLoading={savingTag || false} />
                  </div>
                </Grid.Column>
                <Grid.Column width={8}>
                  <div className="tag-column">
                    <h3>Tags In-Use</h3>
                    <FormTags formId={props.formId} />
                  </div>
                </Grid.Column>
              </Grid>
            </GfModal>
          </Provider>
        </DndProvider>
      )}
    </ReactReduxContext.Consumer>
  );
}

const TagsModal = connect(null, mapDispatchToProps)(ConnectedTagsModal);

export default TagsModal;
